"use client";

import { forwardRef } from "react";
import { cn } from "@/lib/utils";

type SelectSize = "default" | "compact";

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Size variant: "default" for user-facing forms, "compact" for admin/dense layouts */
  selectSize?: SelectSize;
  /** Show error styling (red border and focus ring) */
  error?: boolean;
  /** Convenience prop: array of options to render. Also accepts children for full control. */
  options?: SelectOption[];
}

const sizeStyles: Record<SelectSize, string> = {
  default: "px-4 py-3 pr-10 rounded-xl",
  compact: "px-3 py-2 pr-9 text-sm rounded-lg",
};

/**
 * Select - Styled native select element with custom chevron
 *
 * Sizes:
 * - default: Standard size for user-facing forms (rounded-xl, comfortable padding)
 * - compact: Smaller size for admin/dense layouts (rounded-lg, tighter padding)
 *
 * Features:
 * - Custom dropdown chevron icon replacing native appearance
 * - Accepts `options` prop for convenience or `children` for full control
 * - Error state with red border and focus ring
 * - Disabled state with reduced opacity
 * - Full-width by default
 * - forwardRef for form library compatibility
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { selectSize = "default", error = false, options, children, className, disabled, ...props },
    ref
  ) => {
    return (
      <div className="relative w-full">
        <select
          ref={ref}
          disabled={disabled}
          aria-invalid={error || undefined}
          className={cn(
            // Base styles
            "w-full appearance-none border bg-white text-foreground transition-colors",
            // Size styles
            sizeStyles[selectSize],
            // Focus styles
            error
              ? "border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
              : "border-surface-dim focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary",
            // Disabled state
            disabled && "opacity-50 cursor-not-allowed bg-surface-container-low",
            className
          )}
          {...props}
        >
          {options
            ? options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            : children}
        </select>
        {/* Custom chevron icon */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-foreground-muted">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    );
  }
);

Select.displayName = "Select";
